import { type INode, parse, stringify } from 'svgson';
import svgpath from 'svgpath';
import { ImageUtil } from './util.image';
import { CUTLINER_OPTS } from '../Constant/stickerizer.constant';
import { Prof } from '@modules/Util/Logger/S/Function/prof.ts';

/**
 * Type for SVG path style properties.
 */
export type SvgPathStyle = {
  fill?: string;
  stroke?: string;
  strokeWidth?: string;
  strokeDasharray?: string;
  [key: string]: string | undefined;
};

type Size = {
  width: number;
  height: number;
};

const DATA_URI_PROMISE_CACHE = new Map<string, Promise<string>>();

/**
 * SVG 관련 유틸리티 함수들을 static 메서드로 제공하는 클래스
 */
export class SvgUtil {
  /**
   * URL을 base64 data URI로 변환하되, 결과 Promise를 캐시해서
   * 같은 URL에 대한 중복 호출을 방지합니다.
   */
  static async urlToDataUriCached(url: string): Promise<string> {
    if (!url || url.startsWith('data:')) return url;

    const cached = DATA_URI_PROMISE_CACHE.get(url);
    if (cached) return cached;

    const job = (async () => {
      const dataUri = await ImageUtil.urlToDataUri(url);
      return dataUri;
    })();

    DATA_URI_PROMISE_CACHE.set(url, job);
    return job;
  }

  /**
   * HEX 색상 문자열을 [R, G, B] 배열로 변환합니다. (예: #d22486 → [210, 36, 134])
   * @param hex - HEX 색상 문자열
   * @returns [number, number, number] | null
   */
  static hexToRgb(hex: string): [number, number, number] | null {
    let hexString = hex.trim();
    if (hexString.startsWith('#')) hexString = hexString.slice(1);
    if (hexString.length === 3) {
      hexString = hexString
        .split('')
        .map((c) => c + c)
        .join('');
    }
    if (hexString.length !== 6) return null;
    const num = parseInt(hexString, 16);
    if (isNaN(num)) return null;
    return [(num >> 16) & 0xff, (num >> 8) & 0xff, num & 0xff];
  }

  /**
   * 지정한 RGB 정수 기준으로, 퍼센트 기반 SVG 색상 문자열이 일치하는지 검사합니다.
   * @param colorString - 예: "rgb(82.398987%, 14.099121%, 52.49939%)" 또는 "#d22486"
   * @param targetRgb - 정수 RGB 값 [R, G, B], 예: [210, 36, 134]
   * @param tolerance - 허용 오차 (0~255 기준), 기본값 1
   * @returns boolean - 일치 여부
   */
  static isMatchingRgb(colorString: string, targetRgb: [number, number, number], tolerance = 1): boolean {
    // HEX 코드인 경우
    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(colorString.trim())) {
      const parsedHex = SvgUtil.hexToRgb(colorString);
      if (!parsedHex) return false;
      return parsedHex.every((val, i) => {
        const targetValue = targetRgb[i];
        if (targetValue === undefined) {
          throw new Error(`Invalid target RGB value at index ${i}`);
        }
        return Math.abs(val - targetValue) <= tolerance;
      });
    }
    // rgb(%,%,%) 형태인 경우
    const match = colorString.match(/rgb\(([-\d.]+)%\s*,\s*([-\d.]+)%\s*,\s*([-\d.]+)%\s*\)/i);
    if (!match) return false;
    const [, r, g, b] = match as [string, string, string, string];
    const parsedRgb: [number, number, number] = [
      Math.round((parseFloat(r) / 100) * 255),
      Math.round((parseFloat(g) / 100) * 255),
      Math.round((parseFloat(b) / 100) * 255),
    ];
    return parsedRgb.every((val, i) => {
      const targetValue = targetRgb[i];
      if (targetValue === undefined) {
        throw new Error(`Invalid target RGB value at index ${i}`);
      }
      return Math.abs(val - targetValue) <= tolerance;
    });
  }

  /**
   * Replace <path> styles using svgson: removes class/id/style, applies given style as direct attributes.
   * @param svgString - The SVG string to modify
   * @param style - SvgPathStyle object
   * @returns Promise<string> - Modified SVG string
   */
  static async replacePathStyles(svgString: string, style: SvgPathStyle): Promise<string>;
  /**
   * Replace <path> styles using svgson: removes class/id/style, applies given style as direct attributes.
   * @param svgJson - The SVG INode to modify
   * @param style - SvgPathStyle object
   * @returns Promise<INode> - Modified SVG INode
   */
  static async replacePathStyles(svgJson: INode, style: SvgPathStyle): Promise<INode>;
  static async replacePathStyles(
    svgStringOrJson: string | INode,
    style: SvgPathStyle,
  ): Promise<string | INode> {
    let svgJson: INode;

    if (typeof svgStringOrJson === 'string') {
      svgJson = await parse(svgStringOrJson);
    } else {
      svgJson = svgStringOrJson;
    }

    function applyStylesToPath(node: INode): INode {
      if (node.name === 'path') {
        delete node.attributes.class;
        delete node.attributes.id;
        delete node.attributes.style;
        Object.entries(style).forEach(([key, val]) => {
          if (val !== undefined) {
            node.attributes[key] = val;
          }
        });
      }
      if (node.children && node.children.length) {
        node.children = node.children.map(applyStylesToPath);
      }
      return node;
    }
    const updatedJson = applyStylesToPath(svgJson);

    // Return the appropriate type based on input
    if (typeof svgStringOrJson === 'string') {
      return stringify(updatedJson);
    } else {
      return updatedJson;
    }
  }

  /**
   * SVG 문자열에 대해 scale factor를 적용해 width/height, viewBox, 각 요소의 좌표를 재계산합니다.
   * @param svgString - SVG 문자열
   * @param scale - 스케일 팩터 (예: 2)
   * @param precision - 좌표 계산 시 소수점 정확도 (기본값: 3)
   * @returns Promise<string> - 스케일이 적용된 SVG 문자열
   */
  static async scaleSvgByRecalculating(svgString: string, scale: number, precision = 3): Promise<string> {
    const svgJson = await parse(svgString);

    // 정확도에 따른 반올림 헬퍼 함수
    const roundToPrecision = (value: number): number => {
      return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision);
    };

    const scaleNumericAttr = (attrs: Record<string, string>, keys: string[]) => {
      for (const key of keys) {
        if (attrs[key] !== undefined) {
          const n = parseFloat(attrs[key]);
          if (!isNaN(n)) {
            attrs[key] = roundToPrecision(n * scale).toString();
          }
        }
      }
    };

    const transformNode = (node: INode): INode => {
      const name = node.name;
      const attrs = node.attributes || {};

      switch (name) {
        case 'path':
          if (attrs.d) {
            const scaledPath = svgpath(attrs.d).scale(scale).toString();
            // path 데이터의 모든 숫자에 precision 적용
            attrs.d = scaledPath.replace(/([-+]?\d*\.?\d+)/g, (match) => {
              const num = parseFloat(match);
              if (!isNaN(num)) {
                return roundToPrecision(num).toString();
              }
              return match;
            });
          }
          break;
        case 'rect':
          scaleNumericAttr(attrs, ['x', 'y', 'width', 'height', 'rx', 'ry']);
          break;
        case 'circle':
          scaleNumericAttr(attrs, ['cx', 'cy', 'r']);
          break;
        case 'ellipse':
          scaleNumericAttr(attrs, ['cx', 'cy', 'rx', 'ry']);
          break;
        case 'line':
          scaleNumericAttr(attrs, ['x1', 'y1', 'x2', 'y2']);
          break;
        case 'polyline':
        case 'polygon':
          if (attrs.points) {
            attrs.points = attrs.points
              .trim()
              .split(/\s+/)
              .map((pt) =>
                pt
                  .split(',')
                  .map((n) => roundToPrecision(parseFloat(n) * scale).toString())
                  .join(','),
              )
              .join(' ');
          }
          break;
        case 'text':
          scaleNumericAttr(attrs, ['x', 'y', 'font-size']);
          break;
      }

      const scaledChildren = node.children?.map(transformNode) ?? [];

      return { ...node, attributes: attrs, children: scaledChildren };
    };

    const newSvg = { ...svgJson };

    // width/height scale
    const w = parseFloat(newSvg.attributes.width || '0');
    const h = parseFloat(newSvg.attributes.height || '0');
    if (w && h) {
      newSvg.attributes.width = roundToPrecision(w * scale).toString();
      newSvg.attributes.height = roundToPrecision(h * scale).toString();
    }

    // viewBox scale (있는 경우)
    if (newSvg.attributes.viewBox) {
      // 공백 또는 콤마로 구분된 값 모두 지원
      const viewBoxParts = newSvg.attributes.viewBox.split(/[ ,]+/).map(Number);
      if (viewBoxParts.length === 4 && viewBoxParts.every((v) => !isNaN(v))) {
        const [x, y, vw, vh] = viewBoxParts as [number, number, number, number];
        newSvg.attributes.viewBox = [
          roundToPrecision(x * scale),
          roundToPrecision(y * scale),
          roundToPrecision(vw * scale),
          roundToPrecision(vh * scale),
        ].join(' ');
      }
    } else if (w && h) {
      // viewBox가 없는 경우에만 width, height로 viewBox 생성
      newSvg.attributes.viewBox = `0 0 ${w} ${h}`;
    }

    newSvg.children = newSvg.children?.map(transformNode) ?? [];

    return stringify(newSvg);
  }

  /**
   * SVG 문자열의 <svg> 태그 내 width/height를 새로운 값으로 치환합니다.
   * @param svgString - 원본 SVG 문자열
   * @param width - 새 width(px)
   * @param height - 새 height(px)
   * @returns string - width/height가 치환된 SVG 문자열
   */
  static replaceSvgSize(svgString: string, width: number, height: number): string {
    const svgTagMatch = svgString.match(/<svg[^>]*>/i);
    if (!svgTagMatch) throw new Error('No <svg> tag found');
    const svgTag = svgTagMatch[0];
    const newSvgTag = svgTag
      .replace(/width\s*=\s*['"][\d.]+['"]/i, `width="${width}"`)
      .replace(/height\s*=\s*['"][\d.]+['"]/i, `height="${height}"`);
    return svgString.replace(svgTag, newSvgTag);
  }

  /**
   * SVG 문자열에 대해 scale factor 또는 size를 받아 viewBox/width/height만 조정하여 스케일링합니다.
   * @param svgString - SVG 문자열
   * @param options - { scale?: number, size?: { width: number, height: number } }
   * @returns Promise<string> - 스케일이 적용된 SVG 문자열
   */
  static sizeScaleSvg(
    svgString: string,
    options: { scale?: number; size?: { width: number; height: number } },
  ): string {
    const { width: origWidth, height: origHeight } = this.extractSvgSize(svgString);
    let scale: number;
    if (typeof options.scale === 'number') {
      scale = options.scale;
    } else if (options.size) {
      const { width, height } = options.size;
      scale = Math.min(width / origWidth, height / origHeight);
    } else {
      throw new Error('Either scale or size must be provided');
    }
    const newWidth = +(origWidth * scale).toFixed(2);
    const newHeight = +(origHeight * scale).toFixed(2);
    return this.replaceSvgSize(svgString, newWidth, newHeight);
  }

  /*
  1. 추가 메서드
     - static async putImageToSvgWithLink(svgString: string, imageUrl: string, padding: number): Promise<string>
       - embed 대신 href로 URL 참조하는 방식 (브라우저 환경, xlink:href 제거)
  2. 추가 메서드
    - static async convertImageLinkToEmbedInSvg(svgString: string): Promise<string>
      - svg 요소 중 href로 되어 있는 image 요소를 찾아서 참조하는 이미지를 다운로드 하고 base64 dataUri 로 변환하여 embed 형태로 전환
  */

  /**
   * Extract SVG dimensions and viewBox information
   * @param svgString - SVG string to analyze
   * @returns Object with SVG dimensions and viewBox data
   */
  private static extractSvgInfo(svgString: string): {
    svgTag: string;
    svgWidth: number;
    svgHeight: number;
    viewBox: { x: number; y: number; width: number; height: number };
    scaleX: number;
    scaleY: number;
  } {
    const svgTagMatch = svgString.match(/<svg[^>]*>/i);
    if (!svgTagMatch) throw new Error('No <svg> tag found');
    const svgTag = svgTagMatch[0];

    const widthMatch = svgTag.match(/width\s*=\s*['"]([\d.]+)['"]/i);
    const heightMatch = svgTag.match(/height\s*=\s*['"]([\d.]+)['"]/i);
    if (!widthMatch || !widthMatch[1] || !heightMatch || !heightMatch[1])
      throw new Error('SVG must have width and height attributes');

    const svgWidth = parseFloat(widthMatch[1]);
    const svgHeight = parseFloat(heightMatch[1]);
    if (!svgWidth || !svgHeight) throw new Error('Invalid SVG width/height');

    // viewBox 추출
    const viewBoxMatch = svgTag.match(/viewBox\s*=\s*['"]([\d.\s-]+)['"]/i);
    let vbX = 0;
    let vbY = 0;
    let vbW = svgWidth;
    let vbH = svgHeight;
    if (viewBoxMatch && viewBoxMatch[1]) {
      const parts = viewBoxMatch[1].split(/\s+/).map(Number);
      if (parts.length === 4 && parts.every((v) => !isNaN(v))) {
        vbX = parts[0] ?? 0;
        vbY = parts[1] ?? 0;
        vbW = parts[2] ?? svgWidth;
        vbH = parts[3] ?? svgHeight;
      }
    }

    // scale 계산 (svg px : viewBox 단위)
    const scaleX = svgWidth / vbW;
    const scaleY = svgHeight / vbH;

    return {
      svgTag,
      svgWidth,
      svgHeight,
      viewBox: { x: vbX, y: vbY, width: vbW, height: vbH },
      scaleX,
      scaleY,
    };
  }

  /**
   * Calculate image position and size within SVG based on padding
   * @param svgInfo - SVG information from extractSvgInfo
   * @param padding - Padding in pixels
   * @returns Image dimensions and position in viewBox coordinates
   */
  private static calculateImageDimensions(
    svgInfo: {
      viewBox: { x: number; y: number; width: number; height: number };
      scaleX: number;
      scaleY: number;
    },
    padding: number,
  ): { x: number; y: number; width: number; height: number } {
    const { viewBox, scaleX, scaleY } = svgInfo;

    // padding을 viewBox 단위로 환산
    const vbPaddingX = padding / scaleX;
    const vbPaddingY = padding / scaleY;

    // 이미지 위치/크기 계산 (viewBox 기준)
    const imgX = viewBox.x + vbPaddingX;
    const imgY = viewBox.y + vbPaddingY;
    const imgWidth = viewBox.width - vbPaddingX * 2;
    const imgHeight = viewBox.height - vbPaddingY * 2;

    if (imgWidth <= 0 || imgHeight <= 0) throw new Error('Padding too large for SVG size');

    return { x: imgX, y: imgY, width: imgWidth, height: imgHeight };
  }

  /**
   * URL을 base64 data URI로 변환합니다.
   * @param url - 변환할 이미지 URL
   * @returns Promise<string> - base64 data URI
   */

  static async urlToDataUri(url: string): Promise<string> {
    return ImageUtil.urlToDataUri(url);
  }

  /**
   * Insert image elements into SVG string
   * @param svgString - Original SVG string
   * @param svgTag - Original SVG tag
   * @param defsTag - Defs element containing image definition
   * @param useTag - Use element with href
   * @returns SVG string with image elements inserted
   */
  private static insertImageElements(
    svgString: string,
    svgTag: string,
    defsTag: string,
    useTag: string,
  ): string {
    const svgOpenTagEnd = svgString.indexOf('>');
    if (svgOpenTagEnd === -1) throw new Error('Malformed SVG');
    const isSelfClosing = svgTag.endsWith('/>');
    if (isSelfClosing) throw new Error('Self-closing <svg/> not supported');

    // SVG 닫는 태그 위치 찾기
    const closingTagIndex = svgString.lastIndexOf('</svg>');
    if (closingTagIndex === -1) throw new Error('No closing </svg> tag found');

    // 이미지 요소를 SVG 닫는 태그 직전에 삽입
    const before = svgString.slice(0, closingTagIndex);
    const after = svgString.slice(closingTagIndex);

    return `${before}${defsTag}${useTag}${after}`;
  }

  /**
   * SVG 문자열에 이미지를 URL 링크로 중앙에 padding만큼 줄여서 추가합니다.
   * @param svgString - 원본 SVG 문자열
   * @param imageUrl - 삽입할 이미지의 URL (URL 그대로 참조)
   * @param padding - 이미지와 SVG 경계 사이의 padding(px)
   * @returns Promise<string> - 이미지가 추가된 SVG 문자열
   */
  static async putImageToSvgWithLink(svgString: string, imageUrl: string, padding: number): Promise<string> {
    // 1. SVG 정보 추출
    const svgInfo = this.extractSvgInfo(svgString);

    // 2. 이미지 위치/크기 계산 (viewBox 기준)
    const imageDimensions = this.calculateImageDimensions(svgInfo, padding);

    // 3. <defs><image id="img1" ... /></defs> 및 <use> 태그 생성 (viewBox 기준 좌표)
    const imageId = 'design-image-svg';
    const defsTag = `<defs><image id="${imageId}" x="${imageDimensions.x}" y="${imageDimensions.y}" width="${imageDimensions.width}" height="${imageDimensions.height}" href="${imageUrl}"/></defs>`;
    const useTag = `<use href="#${imageId}" />`;

    // 4. SVG에 이미지 요소들 삽입
    return this.insertImageElements(svgString, svgInfo.svgTag, defsTag, useTag);
  }

  /**
   * SVG 문자열에 이미지를 중앙에 padding만큼 줄여서 추가합니다.
   * @param svgString - 원본 SVG 문자열
   * @param imageUrl - 삽입할 이미지의 URL (base64 datauri로 변환됨)
   * @param padding - 이미지와 SVG 경계 사이의 padding(px)
   * @returns Promise<string> - 이미지가 추가된 SVG 문자열
   */
  static async putImageToSvgWithEmbed(svgString: string, imageUrl: string, padding: number): Promise<string> {
    // 1. SVG 정보 추출
    const svgInfo = this.extractSvgInfo(svgString);

    // 2. 이미지 위치/크기 계산 (viewBox 기준)
    const imageDimensions = this.calculateImageDimensions(svgInfo, padding);

    // 3. 이미지 base64 datauri 변환
    const dataUri = await this.urlToDataUriCached(imageUrl);

    // 4. <defs><image id="img1" ... /></defs> 및 <use> 태그 생성 (viewBox 기준 좌표)
    const imageId = 'design-image-svg';
    const defsTag = `<defs><image id="${imageId}" x="${imageDimensions.x}" y="${imageDimensions.y}" width="${imageDimensions.width}" height="${imageDimensions.height}" href="${dataUri}"/></defs>`;
    const useTag = `<use href="#${imageId}" />`;

    // 5. SVG에 이미지 요소들 삽입
    return this.insertImageElements(svgString, svgInfo.svgTag, defsTag, useTag);
  }

  /**
   * SVG 문자열의 width/height(px, 96dpi 기준)를 300dpi 기준 px로 변환하여 반환합니다.
   * @param svgString - 원본 SVG 문자열 (px 단위 width/height, 96dpi 기준)
   * @returns string - 300dpi 기준 px로 변환된 SVG 문자열
   */
  static to300dpi(svgString: string): string {
    return this.sizeScaleSvg(svgString, { scale: CUTLINER_OPTS.WEB_TO_DPI_300_RATIO });
  }

  /**
   * 현재 실행 환경이 브라우저(window+document)인지 여부를 반환
   */
  private static isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof window.document !== 'undefined';
  }

  /**
   * SVG 문자열을 drawImage로 캔버스에 rasterize합니다.
   * @param svgString - SVG 소스 문자열
   * @returns Promise<HTMLCanvasElement>
   */
  static async toRasterize(svgString: string): Promise<HTMLCanvasElement> {
    let svgUrl: string;
    if (this.isBrowser() && 'createImageBitmap' in window) {
      svgUrl = this.getSvgBlobUrl(svgString);
    } else {
      svgUrl = this.getSvgDataUri(svgString);
    }

    return (await ImageUtil.makeCanvasByUrl(svgUrl)) as HTMLCanvasElement;
  }

  static extractSvgSize(svgString: string): { width: number; height: number } {
    const svgTag = svgString.match(/<svg[^>]*>/i)?.[0];
    if (!svgTag) throw new Error('No <svg> tag found');
    const width = parseFloat(svgTag.match(/width\s*=\s*['"]([\d.]+)['"]/i)?.[1] ?? '');
    const height = parseFloat(svgTag.match(/height\s*=\s*['"]([\d.]+)['"]/i)?.[1] ?? '');
    if (!width || !height) throw new Error('SVG must have width and height attributes');
    return { width, height };
  }

  static getSvgBlobUrl(svgString: string): string {
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    return URL.createObjectURL(blob);
  }

  // Node.js에서 사용할 data URI 생성 함수
  static getSvgDataUri(svgString: string): string {
    return 'data:image/svg+xml;base64,' + Buffer.from(svgString).toString('base64');
  }

  /**
   * SVG 문자열에서 <image> 요소만 분리하여 두 개의 SVG 문자열로 반환합니다.
   * - svgHavingImage: 각 image 요소가 원래 렌더링된 컨텍스트(부모 chain)를 모두 유지, image 외 다른 sibling은 제거
   * - svgExcludeImage: 모든 image 요소를 재귀적으로 제거
   * @param svgString - 원본 SVG 문자열
   * @returns Promise<{ svgHavingImage: string, svgExcludeImage: string }>
   */
  static async splitImageFromSvg(
    svgString: string,
  ): Promise<{ svgHavingImage: string; svgExcludeImage: string }> {
    const svgJson = await parse(svgString);

    if (!Array.isArray(svgJson.children)) throw new Error('Invalid SVG: children must be array');

    // 1. <defs> 내 <image> id 수집
    const imageIdSet = new Set<string>();
    function collectImageIds(node: INode) {
      if (node.name === 'defs' && Array.isArray(node.children)) {
        for (const child of node.children) {
          if (child.name === 'image' && child.attributes?.id) {
            imageIdSet.add(`#${child.attributes.id}`);
          }
        }
      }
      if (node.children) node.children.forEach(collectImageIds);
    }
    collectImageIds(svgJson);

    // 3. removeImageRecursively: <image>와 <use>가 <image>를 참조하면 모두 제거
    function removeImageRecursively(node: INode): INode | null {
      if (node.name === 'image') return null;
      if (node.name === 'use') {
        const href = node.attributes?.href;
        if (href && imageIdSet.has(href)) {
          return null;
        }
      }
      if (!node.children || node.children.length === 0) return node;
      const filteredChildren = node.children.map(removeImageRecursively).filter(Boolean) as INode[];
      return { ...node, children: filteredChildren };
    }

    // 2. extractImageSubtrees: <image> 또는 <use>가 <image>를 참조하면 부모 chain 유지
    function extractImageSubtrees(node: INode): INode | null {
      // <image> 직접
      if (node.name === 'image') return { ...node };
      // <use>가 <image>를 참조하는 경우
      if (node.name === 'use') {
        const href = node.attributes?.href;
        if (href && imageIdSet.has(href)) {
          return { ...node };
        }
        return null;
      }
      if (!node.children || node.children.length === 0) return null;
      const imageChildren = node.children.map(extractImageSubtrees).filter(Boolean) as INode[];
      if (imageChildren.length === 0) return null;
      return { ...node, children: imageChildren };
    }

    // svgHavingImage: <image> 또는 <use>가 <image>를 참조하는 것만 남기고 부모 chain 유지
    const imageTree = extractImageSubtrees(svgJson);
    const svgHavingImage = stringify(imageTree || { ...svgJson, children: [] });

    // svgExcludeImage: <image>와 <use>가 <image>를 참조하는 것 모두 제거
    const excludeTree = removeImageRecursively(svgJson);
    const svgExcludeImage = stringify(excludeTree || { ...svgJson, children: [] });
    return { svgHavingImage, svgExcludeImage };
  }

  /**
   * SVG의 viewBox를 0 0 width height로 정규화합니다.
   * 기존 viewBox가 있을 때는 translate와 scale을 계산하여 모든 path 요소의 좌표를 재계산하고,
   * 렌더링 결과는 동일하게 유지하면서 viewBox만 정규화합니다.
   * @param svgString - 원본 SVG 문자열
   * @param precision - 좌표 계산 시 소수점 정확도 (기본값: 4)
   * @param scale - 추가 스케일 팩터 (기본값: 1)
   * @returns Promise<string> - viewBox가 정규화된 SVG 문자열
   */
  static async normalizeViewBoxWithScale(svgString: string, precision = 4, scale = 1): Promise<string> {
    // 1. SVG의 width/height 추출
    const { width, height } = this.extractSvgSize(svgString);

    // 2. SVG JSON 파싱
    const svgJson = await parse(svgString);

    // 3. 현재 viewBox 분석
    const currentViewBox = svgJson.attributes?.viewBox;
    let translateX = 0;
    let translateY = 0;
    let scaleX = 1;
    let scaleY = 1;

    if (currentViewBox && typeof currentViewBox === 'string') {
      const viewBoxParts = currentViewBox.split(/\s+/).map(Number);
      if (viewBoxParts.length === 4 && viewBoxParts.every((v) => !isNaN(v))) {
        const [vbX, vbY, vbWidth, vbHeight] = viewBoxParts;
        if (vbX !== undefined && vbY !== undefined && vbWidth !== undefined && vbHeight !== undefined) {
          // viewBox에서 실제 좌표계로 변환하기 위한 변환 계산
          // 현재 viewBox (vbX, vbY, vbWidth, vbHeight)를 (0, 0, width, height)로 변환
          // 추가 스케일 팩터도 함께 적용
          scaleX = (width / vbWidth) * scale;
          scaleY = (height / vbHeight) * scale;
          translateX = -vbX * scaleX;
          translateY = -vbY * scaleY;
        }
      }
    } else {
      // viewBox가 없는 경우에도 scale 팩터만 적용
      scaleX = scale;
      scaleY = scale;
    }

    // 4. 모든 요소의 좌표를 재계산하는 함수
    const transformNode = (node: INode): INode => {
      const name = node.name;
      const attrs = { ...(node.attributes || {}) };

      // 정확도에 따른 반올림 헬퍼 함수
      const roundToPrecision = (value: number): number => {
        return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision);
      };

      // 숫자 속성을 변환하는 헬퍼 함수
      const transformCoordinate = (value: string | undefined, isX: boolean): string | undefined => {
        if (!value) return value;
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        const scale = isX ? scaleX : scaleY;
        const translate = isX ? translateX : translateY;
        const transformed = roundToPrecision(num * scale + translate);
        return transformed.toString();
      };

      const transformNumericAttr = (
        attrs: Record<string, string>,
        coordKeys: { key: string; isX: boolean }[],
      ) => {
        for (const { key, isX } of coordKeys) {
          if (attrs[key] !== undefined) {
            const transformed = transformCoordinate(attrs[key], isX);
            if (transformed !== undefined) {
              attrs[key] = transformed;
            }
          }
        }
      };

      switch (name) {
        case 'path':
          if (attrs.d && typeof attrs.d === 'string') {
            // viewBox 변환을 올바르게 적용하기 위해 matrix 변환 사용
            // 변환 공식: newCoord = (oldCoord - offset) * scale
            // 이는 translate(-offset) -> scale(scale) 순서와 동일
            let transformedPath;

            if (currentViewBox && typeof currentViewBox === 'string') {
              const viewBoxParts = currentViewBox.split(/\s+/).map(Number);
              if (viewBoxParts.length === 4 && viewBoxParts.every((v) => !isNaN(v))) {
                const vbX = viewBoxParts[0]!;
                const vbY = viewBoxParts[1]!;
                const vbWidth = viewBoxParts[2]!;
                const vbHeight = viewBoxParts[3]!;

                // viewBox 변환: 먼저 viewBox 원점으로 이동 -> 스케일 적용
                const finalScaleX = (width / vbWidth) * scale;
                const finalScaleY = (height / vbHeight) * scale;
                transformedPath = svgpath(attrs.d).translate(-vbX, -vbY).scale(finalScaleX, finalScaleY);
              } else {
                // viewBox 파싱 실패 시 기본 스케일만 적용
                transformedPath = svgpath(attrs.d).scale(scaleX, scaleY);
              }
            } else {
              // viewBox가 없는 경우 스케일만 적용
              transformedPath = svgpath(attrs.d).scale(scaleX, scaleY);
            }

            // 변환된 path 데이터를 문자열로 변환하고 precision 적용
            let pathData = transformedPath.toString();

            // 다른 요소들과 동일한 precision 적용
            pathData = pathData.replace(/([-+]?\d*\.?\d+)/g, (match) => {
              const num = parseFloat(match);
              if (!isNaN(num)) {
                return roundToPrecision(num).toString();
              }
              return match;
            });

            attrs.d = pathData;
          }
          break;
        case 'rect':
          // 위치 좌표 (x, y)에는 scale과 translate 모두 적용
          transformNumericAttr(attrs, [
            { key: 'x', isX: true },
            { key: 'y', isX: false },
          ]);
          // 크기 (width, height)와 radius (rx, ry)에는 scale만 적용 (translate 제외)
          if (attrs.width) {
            const width = parseFloat(attrs.width);
            if (!isNaN(width)) {
              const scaledWidth = roundToPrecision(width * scaleX);
              attrs.width = scaledWidth.toString();
            }
          }
          if (attrs.height) {
            const height = parseFloat(attrs.height);
            if (!isNaN(height)) {
              const scaledHeight = roundToPrecision(height * scaleY);
              attrs.height = scaledHeight.toString();
            }
          }
          if (attrs.rx) {
            const rx = parseFloat(attrs.rx);
            if (!isNaN(rx)) {
              const scaledRx = roundToPrecision(rx * scaleX);
              attrs.rx = scaledRx.toString();
            }
          }
          if (attrs.ry) {
            const ry = parseFloat(attrs.ry);
            if (!isNaN(ry)) {
              const scaledRy = roundToPrecision(ry * scaleY);
              attrs.ry = scaledRy.toString();
            }
          }
          break;
        case 'circle':
          transformNumericAttr(attrs, [
            { key: 'cx', isX: true },
            { key: 'cy', isX: false },
          ]);
          // 반지름은 스케일의 평균값 적용
          if (attrs.r) {
            const r = parseFloat(attrs.r);
            if (!isNaN(r)) {
              const scaledR = roundToPrecision(r * Math.sqrt(scaleX * scaleY));
              attrs.r = scaledR.toString();
            }
          }
          break;
        case 'ellipse':
          // 위치 좌표 (cx, cy)에는 scale과 translate 모두 적용
          transformNumericAttr(attrs, [
            { key: 'cx', isX: true },
            { key: 'cy', isX: false },
          ]);
          // 반지름 (rx, ry)에는 scale만 적용 (translate 제외)
          if (attrs.rx) {
            const rx = parseFloat(attrs.rx);
            if (!isNaN(rx)) {
              const scaledRx = roundToPrecision(rx * scaleX);
              attrs.rx = scaledRx.toString();
            }
          }
          if (attrs.ry) {
            const ry = parseFloat(attrs.ry);
            if (!isNaN(ry)) {
              const scaledRy = roundToPrecision(ry * scaleY);
              attrs.ry = scaledRy.toString();
            }
          }
          break;
        case 'line':
          transformNumericAttr(attrs, [
            { key: 'x1', isX: true },
            { key: 'y1', isX: false },
            { key: 'x2', isX: true },
            { key: 'y2', isX: false },
          ]);
          break;
        case 'polyline':
        case 'polygon':
          if (attrs.points) {
            attrs.points = attrs.points
              .trim()
              .split(/\s+/)
              .map((pt) => {
                const coords = pt.split(',');
                if (coords.length === 2) {
                  const x = parseFloat(coords[0]!);
                  const y = parseFloat(coords[1]!);
                  if (!isNaN(x) && !isNaN(y)) {
                    const newX = roundToPrecision(x * scaleX + translateX);
                    const newY = roundToPrecision(y * scaleY + translateY);
                    return `${newX},${newY}`;
                  }
                }
                return pt;
              })
              .join(' ');
          }
          break;
        case 'text':
          transformNumericAttr(attrs, [
            { key: 'x', isX: true },
            { key: 'y', isX: false },
          ]);
          // font-size는 스케일 평균값 적용
          if (attrs['font-size']) {
            const fontSize = parseFloat(attrs['font-size']);
            if (!isNaN(fontSize)) {
              const scaledFontSize = roundToPrecision(fontSize * Math.sqrt(scaleX * scaleY));
              attrs['font-size'] = scaledFontSize.toString();
            }
          }
          break;
        case 'image':
          // 이미지의 위치 좌표 (x, y)에는 scale과 translate 모두 적용
          transformNumericAttr(attrs, [
            { key: 'x', isX: true },
            { key: 'y', isX: false },
          ]);
          // 이미지의 크기 (width, height)에는 scale만 적용 (translate 제외)
          if (attrs.width) {
            const width = parseFloat(attrs.width);
            if (!isNaN(width)) {
              const scaledWidth = roundToPrecision(width * scaleX);
              attrs.width = scaledWidth.toString();
            }
          }
          if (attrs.height) {
            const height = parseFloat(attrs.height);
            if (!isNaN(height)) {
              const scaledHeight = roundToPrecision(height * scaleY);
              attrs.height = scaledHeight.toString();
            }
          }
          break;
      }

      // transform 속성이 있다면 추가 변환 적용
      if (attrs.transform) {
        // 기존 transform에 새로운 변환을 앞에 추가
        const roundedScaleX = roundToPrecision(scaleX);
        const roundedScaleY = roundToPrecision(scaleY);
        const roundedTranslateX = roundToPrecision(translateX);
        const roundedTranslateY = roundToPrecision(translateY);
        const newTransform = `matrix(${roundedScaleX},0,0,${roundedScaleY},${roundedTranslateX},${roundedTranslateY})`;
        attrs.transform = `${newTransform} ${attrs.transform}`;
      } else if (scaleX !== 1 || scaleY !== 1 || translateX !== 0 || translateY !== 0) {
        // transform이 없고 변환이 필요한 경우에만 추가
        // 하지만 이미 개별 좌표를 변환했으므로 일반적으로는 불필요
      }

      // 자식 노드들도 재귀적으로 변환
      const transformedChildren = node.children?.map(transformNode) ?? [];

      return { ...node, attributes: attrs, children: transformedChildren };
    };

    // 5. 모든 노드 변환
    const transformedSvg = transformNode(svgJson);

    // 6. viewBox를 0 0 width height로 설정 (scale 고려)
    const finalWidth = Math.round(width * scale * Math.pow(10, precision)) / Math.pow(10, precision);
    const finalHeight = Math.round(height * scale * Math.pow(10, precision)) / Math.pow(10, precision);
    transformedSvg.attributes = {
      ...transformedSvg.attributes,
      width: finalWidth.toString(),
      height: finalHeight.toString(),
      viewBox: `0 0 ${finalWidth} ${finalHeight}`,
    };

    // 7. 변환된 SVG 문자열 반환
    let resultSvgString = stringify(transformedSvg);

    // 8. SVG 문자열의 모든 숫자 속성에 precision 적용 (stringify 후처리)
    // precision에 따른 반올림 헬퍼 함수 (외부 스코프용)
    const roundToPrecision = (value: number): number => {
      return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision);
    };

    // 속성값의 숫자들에 precision 적용
    resultSvgString = resultSvgString.replace(
      /(\w+)="([-+]?\d*\.?\d+([eE][-+]?\d+)?)"/g,
      (match, attrName, numStr) => {
        // viewBox는 이미 정확히 설정했으므로 제외
        if (attrName === 'viewBox') return match;

        const num = parseFloat(numStr);
        if (!isNaN(num)) {
          const rounded = roundToPrecision(num);
          return `${attrName}="${rounded}"`;
        }
        return match;
      },
    );

    // 좌표 리스트 (points, d 등)의 숫자들에도 precision 적용
    resultSvgString = resultSvgString.replace(/(points|d)="([^"]+)"/g, (match, attrName, value) => {
      if (attrName === 'viewBox') return match;

      const processedValue = value.replace(/([-+]?\d*\.?\d+([eE][-+]?\d+)?)/g, (numMatch: string) => {
        const num = parseFloat(numMatch);
        if (!isNaN(num)) {
          return roundToPrecision(num).toString();
        }
        return numMatch;
      });

      return `${attrName}="${processedValue}"`;
    });

    return resultSvgString;
  }

  static getSvgDimension(svgString: string, dpi = 300): { px: Size; mm: Size; ratio: number } {
    const precision = 3;
    const { width: width_px, height: height_px } = this.extractSvgSize(svgString);
    const width_mm = Math.round((width_px / dpi) * 25.4 * Math.pow(10, precision)) / Math.pow(10, precision);
    const height_mm =
      Math.round((height_px / dpi) * 25.4 * Math.pow(10, precision)) / Math.pow(10, precision);
    return {
      px: { width: width_px, height: height_px },
      mm: { width: width_mm, height: height_mm },
      ratio: Math.round((width_px / height_px) * Math.pow(10, precision)) / Math.pow(10, precision),
    };
  }

  static rotateSvgPathdata(pathData: string, origin?: { x: number; y: number }): string {
    let path = svgpath(pathData);

    if (origin) {
      // SVG 중심 기준 회전: 이론대로 정확함!
      path = path
        .translate(-origin.x, -origin.y) // 중심을 원점으로
        .rotate(-90) // 원점 기준 -90도 회전
        .translate(origin.x, origin.y); // 다시 원래 중심으로
    } else {
      path = path.rotate(-90);
    }

    return path.toString();
  }

  /**
   * Remove bounding box rectangle from SVG string
   */
  static async removeBoundingBoxRectangle(svgString: string): Promise<string>;
  /**
   * Remove bounding box rectangle from SVG INode
   */
  static async removeBoundingBoxRectangle(svgJson: INode, width: number, height: number): Promise<INode>;
  static async removeBoundingBoxRectangle(
    svgStringOrJson: string | INode,
    width?: number,
    height?: number,
  ): Promise<string | INode> {
    let svgJson: INode;
    let svgWidth: number;
    let svgHeight: number;

    if (typeof svgStringOrJson === 'string') {
      svgJson = await parse(svgStringOrJson);
      const size = this.extractSvgSize(svgStringOrJson);
      svgWidth = size.width;
      svgHeight = size.height;
    } else {
      svgJson = svgStringOrJson;
      if (width === undefined || height === undefined) {
        throw new Error('Width and height must be provided when using INode input');
      }
      svgWidth = width;
      svgHeight = height;
    }

    // Tolerance for size comparison (5% of the dimension)
    const tolerance = Math.max(svgWidth, svgHeight) * 0.05;

    /**
     * Remove bounding box segments from compound path:
     * 1. Split compound path by Z/z closepath commands
     * 2. For each segment, check if it's a bounding box (starts with M/m 0,0 and contains SVG dimensions)
     * 3. Remove only the bounding box segments, keep other segments
     */
    const removeBoundingBoxFromCompoundPath = (pathData: string): string => {
      if (!pathData) return pathData;

      const trimmedPath = pathData.trim();

      // Check if this is a compound path (contains multiple Z/z commands)
      const zMatches = trimmedPath.match(/[Zz]/g);
      if (!zMatches || zMatches.length <= 1) {
        // Single path - check if it's a bounding box
        if (isBoundingBoxSegment(trimmedPath)) {
          return ''; // Remove the entire path if it's a bounding box
        }
        return pathData; // Keep the path if it's not a bounding box
      }

      // Split compound path into segments by Z/z commands
      const segments = trimmedPath.split(/([Zz])/i).reduce((acc, part, index, array) => {
        if (index % 2 === 0 && index < array.length - 1) {
          // Combine path segment with its closing Z/z
          const segment = part + (array[index + 1] || '');
          acc.push(segment);
        }
        return acc;
      }, [] as string[]);

      // Filter out bounding box segments
      const filteredSegments = segments.filter((segment) => !isBoundingBoxSegment(segment));

      return filteredSegments.join(' ').trim();
    };

    /**
     * Check if a single path segment is a bounding box rectangle:
     * 1. Starts with M/m 0,0 (case insensitive)
     * 2. Contains width and height values that match SVG dimensions (within tolerance)
     */
    const isBoundingBoxSegment = (pathSegment: string): boolean => {
      if (!pathSegment) return false;

      const trimmedSegment = pathSegment.trim();

      // Check if segment starts with M/m 0,0 (with various spacing/comma formats)
      const startsWithM00 = /^[Mm]\s*0[,\s]+0/i.test(trimmedSegment);
      if (!startsWithM00) return false;

      // Extract all numbers from the segment and check if width and height values are present
      const numbers = trimmedSegment.match(/([-+]?\d*\.?\d+)/g)?.map(Number) || [];
      if (numbers.length < 4) return false; // Need at least 4 numbers for a rectangle

      // Check if any numbers match the SVG width and height (within tolerance)
      const hasWidth = numbers.some((num) => Math.abs(num - svgWidth) <= tolerance);
      const hasHeight = numbers.some((num) => Math.abs(num - svgHeight) <= tolerance);

      return hasWidth && hasHeight;
    };

    /**
     * Recursively process paths and remove bounding box segments from compound paths
     */
    const removeBoundingBoxRecursively = (node: INode): INode | null => {
      // Process path nodes
      if (node.name === 'path' && node.attributes?.d) {
        const cleanedPath = removeBoundingBoxFromCompoundPath(node.attributes.d);

        // If the cleaned path is empty, remove the entire path node
        if (!cleanedPath.trim()) {
          return null;
        }

        // If the path was modified, update the d attribute
        if (cleanedPath !== node.attributes.d) {
          return {
            ...node,
            attributes: {
              ...node.attributes,
              d: cleanedPath,
            },
          };
        }
      }

      // Process children recursively
      if (node.children && node.children.length > 0) {
        const filteredChildren = node.children.map(removeBoundingBoxRecursively).filter(Boolean) as INode[];

        return { ...node, children: filteredChildren };
      }

      return node;
    };

    // Apply the removal function to the SVG tree
    const cleanedSvgJson = removeBoundingBoxRecursively(svgJson);

    // Return the appropriate type based on input
    if (typeof svgStringOrJson === 'string') {
      return stringify(cleanedSvgJson || svgJson);
    } else {
      return cleanedSvgJson || svgJson;
    }
  }

  /**
   * Remove margin from SVG by translating all elements and adjusting SVG dimensions
   * @param svgStringOrJson - The SVG string or INode to process
   * @param marginPx - The margin size in pixels to remove (default: 2)
   * @param precision - Coordinate calculation precision (default: 4)
   * @returns Promise<string | INode> - SVG with margin removed
   */
  static async removeMarginFromSvg(svgString: string, marginPx?: number): Promise<string>;

  static async removeMarginFromSvg(svgJson: INode, marginPx?: number): Promise<INode>;

  static async removeMarginFromSvg(
    svgStringOrJson: string | INode,
    marginPx = CUTLINER_OPTS.DEFAULT_IMG_RESIZE_MARGIN,
  ): Promise<string | INode> {
    if (marginPx <= 0) {
      return svgStringOrJson;
    }

    let svgJson: INode;
    let originalWidth: number;
    let originalHeight: number;

    if (typeof svgStringOrJson === 'string') {
      svgJson = await parse(svgStringOrJson);
      const size = this.extractSvgSize(svgStringOrJson);
      originalWidth = size.width;
      originalHeight = size.height;
    } else {
      svgJson = svgStringOrJson;
      const widthAttr = svgJson.attributes?.width;
      const heightAttr = svgJson.attributes?.height;

      if (!widthAttr || !heightAttr) {
        throw new Error('SVG must have width and height attributes for margin removal');
      }

      originalWidth = parseFloat(widthAttr);
      originalHeight = parseFloat(heightAttr);

      if (isNaN(originalWidth) || isNaN(originalHeight) || originalWidth <= 0 || originalHeight <= 0) {
        throw new Error('Invalid SVG width or height attributes');
      }
    }

    // Calculate new dimensions after margin removal
    const newWidth = originalWidth - 2 * marginPx;
    const newHeight = originalHeight - 2 * marginPx;

    if (newWidth <= 0 || newHeight <= 0) {
      throw new Error(
        `Cannot remove margin ${marginPx}px from SVG (${originalWidth}x${originalHeight}): resulting size would be non-positive`,
      );
    }

    // Apply global translate transform to remove margin
    const globalTransform = [1, 0, 0, 1, -marginPx, -marginPx]; // translate(-marginPx, -marginPx)

    // Use flattenTransformAttributes with global transform
    const transformedSvgJson = await this.flattenTransformAttributes(svgJson, 4, globalTransform);

    // Update SVG dimensions and viewBox
    const finalSvgJson: INode = {
      ...transformedSvgJson,
      attributes: {
        ...transformedSvgJson.attributes,
        width: newWidth.toString(),
        height: newHeight.toString(),
        viewBox: `0 0 ${newWidth} ${newHeight}`,
      },
    };

    // Return the appropriate type based on input
    if (typeof svgStringOrJson === 'string') {
      return stringify(finalSvgJson);
    } else {
      return finalSvgJson;
    }
  }

  /**
   * SVG 요소들의 transform 속성을 모두 찾아서 직접 좌표값에 적용하고 transform 속성을 제거합니다.
   * 성능 최적화: translate(0,0), scale(1), rotate(0) 등 의미없는 transform은 건너뛰고,
   * 단위 행렬인 경우 좌표 변환을 생략합니다.
   * @param svgString - 원본 SVG 문자열
   * @param precision - 좌표 계산 시 소수점 정확도 (기본값: 4)
   * @param globalTransform - 전역적으로 적용할 transform 매트릭스 [a, b, c, d, e, f] (선택사항)
   * @returns Promise<string> - transform이 플래튼된 SVG 문자열
   */
  static async flattenTransformAttributes(
    svgString: string,
    precision?: number,
    globalTransform?: number[],
  ): Promise<string>;

  static async flattenTransformAttributes(
    svgJson: INode,
    precision?: number,
    globalTransform?: number[],
  ): Promise<INode>;

  static async flattenTransformAttributes(
    svgStringOrJson: string | INode,
    precision = 4,
    globalTransform?: number[],
  ): Promise<string | INode> {
    let svgJson: INode;

    if (typeof svgStringOrJson === 'string') {
      svgJson = await parse(svgStringOrJson);
    } else {
      svgJson = svgStringOrJson;
    }

    // Transform 파싱 및 매트릭스 변환 유틸리티 함수들
    const parseTransformValue = (transformStr: string): Array<{ type: string; values: number[] }> => {
      const transforms: Array<{ type: string; values: number[] }> = [];
      const transformRegex = /(matrix|translate|scale|rotate|skewX|skewY)\s*\(\s*([^)]+)\s*\)/g;

      let match;
      while ((match = transformRegex.exec(transformStr)) !== null) {
        const type = match[1];
        const valuesStr = match[2];
        if (type && valuesStr) {
          const values = valuesStr
            .split(/[\s,]+/)
            .map(Number)
            .filter((n) => !isNaN(n));

          // 의미없는 transform 건너뛰기
          if (isNeutralTransform(type, values)) {
            continue;
          }

          transforms.push({ type, values });
        }
      }

      return transforms;
    };

    // 의미없는 transform인지 확인하는 함수
    const isNeutralTransform = (type: string, values: number[]): boolean => {
      const EPSILON = 1e-10; // 부동소수점 오차 허용

      switch (type) {
        case 'translate':
          // translate(0) 또는 translate(0,0)
          return (
            values.length === 0 ||
            (values.length >= 1 &&
              Math.abs(values[0] ?? 0) < EPSILON &&
              (values.length === 1 || Math.abs(values[1] ?? 0) < EPSILON))
          );

        case 'scale':
          // scale(1) 또는 scale(1,1)
          return (
            values.length === 0 ||
            (values.length >= 1 &&
              Math.abs((values[0] ?? 1) - 1) < EPSILON &&
              (values.length === 1 || Math.abs((values[1] ?? values[0] ?? 1) - 1) < EPSILON))
          );

        case 'rotate':
          // rotate(0) 또는 rotate(0,cx,cy)
          return values.length === 0 || Math.abs(values[0] ?? 0) < EPSILON;

        case 'skewX':
        case 'skewY':
          // skewX(0) 또는 skewY(0)
          return values.length === 0 || Math.abs(values[0] ?? 0) < EPSILON;

        case 'matrix':
          // matrix(1,0,0,1,0,0) - 단위 행렬
          if (values.length < 6) return false;
          return (
            Math.abs((values[0] ?? 1) - 1) < EPSILON &&
            Math.abs(values[1] ?? 0) < EPSILON &&
            Math.abs(values[2] ?? 0) < EPSILON &&
            Math.abs((values[3] ?? 1) - 1) < EPSILON &&
            Math.abs(values[4] ?? 0) < EPSILON &&
            Math.abs(values[5] ?? 0) < EPSILON
          );

        default:
          return false;
      }
    };

    // 매트릭스가 단위 행렬인지 확인하는 함수
    const isIdentityMatrix = (matrix: number[]): boolean => {
      const EPSILON = 1e-10;
      const identity = [1, 0, 0, 1, 0, 0];
      return matrix.every((val, idx) => Math.abs(val - (identity[idx] ?? 0)) < EPSILON);
    };

    const transformToMatrix = (transforms: Array<{ type: string; values: number[] }>): number[] => {
      // 빈 배열이면 단위 행렬 반환
      if (transforms.length === 0) {
        return [1, 0, 0, 1, 0, 0];
      }

      let matrix = [1, 0, 0, 1, 0, 0]; // 단위 행렬 [a, b, c, d, e, f]

      for (const { type, values } of transforms) {
        let newMatrix: number[];

        switch (type) {
          case 'matrix': {
            newMatrix = values.length >= 6 ? values.slice(0, 6) : [1, 0, 0, 1, 0, 0];
            break;
          }
          case 'translate': {
            const tx = values[0] || 0;
            const ty = values[1] || 0;
            newMatrix = [1, 0, 0, 1, tx, ty];
            break;
          }
          case 'scale': {
            const sx = values[0] || 1;
            const sy = values[1] || sx;
            newMatrix = [sx, 0, 0, sy, 0, 0];
            break;
          }
          case 'rotate': {
            const angle = ((values[0] || 0) * Math.PI) / 180;
            const cos = Math.cos(angle);
            const sin = Math.sin(angle);
            const cx = values[1] || 0;
            const cy = values[2] || 0;
            if (cx !== 0 || cy !== 0) {
              newMatrix = [cos, sin, -sin, cos, -cx * cos + cy * sin + cx, -cx * sin - cy * cos + cy];
            } else {
              newMatrix = [cos, sin, -sin, cos, 0, 0];
            }
            break;
          }
          case 'skewX': {
            const skewAngleX = ((values[0] || 0) * Math.PI) / 180;
            newMatrix = [1, 0, Math.tan(skewAngleX), 1, 0, 0];
            break;
          }
          case 'skewY': {
            const skewAngleY = ((values[0] || 0) * Math.PI) / 180;
            newMatrix = [1, Math.tan(skewAngleY), 0, 1, 0, 0];
            break;
          }
          default:
            newMatrix = [1, 0, 0, 1, 0, 0];
        }

        // 매트릭스 곱셈: matrix = matrix * newMatrix
        const [a1, b1, c1, d1, e1, f1] = matrix;
        const [a2, b2, c2, d2, e2, f2] = newMatrix;

        matrix = [
          (a1 ?? 0) * (a2 ?? 0) + (c1 ?? 0) * (b2 ?? 0),
          (b1 ?? 0) * (a2 ?? 0) + (d1 ?? 0) * (b2 ?? 0),
          (a1 ?? 0) * (c2 ?? 0) + (c1 ?? 0) * (d2 ?? 0),
          (b1 ?? 0) * (c2 ?? 0) + (d1 ?? 0) * (d2 ?? 0),
          (a1 ?? 0) * (e2 ?? 0) + (c1 ?? 0) * (f2 ?? 0) + (e1 ?? 0),
          (b1 ?? 0) * (e2 ?? 0) + (d1 ?? 0) * (f2 ?? 0) + (f1 ?? 0),
        ];
      }

      return matrix;
    };

    const applyMatrixToCoordinates = (matrix: number[], x: number, y: number): [number, number] => {
      const [a, b, c, d, e, f] = matrix;
      const newX = (a ?? 0) * x + (c ?? 0) * y + (e ?? 0);
      const newY = (b ?? 0) * x + (d ?? 0) * y + (f ?? 0);
      return [newX, newY];
    };

    const roundToPrecision = (value: number): number => {
      return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision);
    };

    // 재귀적으로 노드를 변환하는 함수
    const transformNode = (node: INode, parentMatrix: number[] = [1, 0, 0, 1, 0, 0]): INode => {
      const attrs = { ...(node.attributes || {}) };

      // 현재 노드의 transform 속성 처리
      let currentMatrix = [...parentMatrix];

      if (attrs.transform) {
        const transforms = parseTransformValue(attrs.transform);

        // 의미있는 transform이 없으면 속성만 제거하고, parentMatrix가 단위행렬이 아니면 좌표 변환 진행
        if (transforms.length === 0) {
          delete attrs.transform;
          // parentMatrix가 단위 행렬이면 조기 반환, 아니면 좌표 변환 진행
          if (isIdentityMatrix(parentMatrix)) {
            const transformedChildren =
              node.children?.map((child) => transformNode(child, parentMatrix)) ?? [];
            return { ...node, attributes: attrs, children: transformedChildren };
          }
          // parentMatrix가 단위 행렬이 아니므로 좌표 변환 진행을 위해 continue
        }

        if (transforms.length > 0) {
          const nodeMatrix = transformToMatrix(transforms);

          // 노드 매트릭스가 단위 행렬이면 변환 불필요
          if (isIdentityMatrix(nodeMatrix)) {
            delete attrs.transform;
            // 자식 노드들을 부모 매트릭스로 변환 (변환 없음)
            const transformedChildren =
              node.children?.map((child) => transformNode(child, parentMatrix)) ?? [];
            return { ...node, attributes: attrs, children: transformedChildren };
          }

          // 부모 매트릭스와 현재 매트릭스를 곱함
          const [a1, b1, c1, d1, e1, f1] = currentMatrix;
          const [a2, b2, c2, d2, e2, f2] = nodeMatrix;

          currentMatrix = [
            (a1 ?? 0) * (a2 ?? 0) + (c1 ?? 0) * (b2 ?? 0),
            (b1 ?? 0) * (a2 ?? 0) + (d1 ?? 0) * (b2 ?? 0),
            (a1 ?? 0) * (c2 ?? 0) + (c1 ?? 0) * (d2 ?? 0),
            (b1 ?? 0) * (c2 ?? 0) + (d1 ?? 0) * (d2 ?? 0),
            (a1 ?? 0) * (e2 ?? 0) + (c1 ?? 0) * (f2 ?? 0) + (e1 ?? 0),
            (b1 ?? 0) * (e2 ?? 0) + (d1 ?? 0) * (f2 ?? 0) + (f1 ?? 0),
          ];

          // transform 속성 제거
          delete attrs.transform;
        }
      }

      // 현재 매트릭스가 단위 행렬이면 좌표 변환 불필요
      if (isIdentityMatrix(currentMatrix)) {
        const transformedChildren =
          node.children?.map((child) => transformNode(child, [1, 0, 0, 1, 0, 0])) ?? [];
        return { ...node, attributes: attrs, children: transformedChildren };
      }

      // 좌표 속성을 현재 매트릭스로 변환
      const name = node.name;

      switch (name) {
        case 'path':
          if (attrs.d && typeof attrs.d === 'string') {
            const [a, b, c, d, e, f] = currentMatrix;
            const transformedPath = svgpath(attrs.d).matrix([a ?? 0, b ?? 0, c ?? 0, d ?? 0, e ?? 0, f ?? 0]);
            let pathData = transformedPath.toString();

            pathData = pathData.replace(/([-+]?\d*\.?\d+)/g, (match) => {
              const num = parseFloat(match);
              if (!isNaN(num)) {
                return roundToPrecision(num).toString();
              }
              return match;
            });

            attrs.d = pathData;
          }
          break;
        case 'rect':
          if (attrs.x !== undefined && attrs.y !== undefined) {
            const x = parseFloat(attrs.x) || 0;
            const y = parseFloat(attrs.y) || 0;
            const width = parseFloat(attrs.width || '0');
            const height = parseFloat(attrs.height || '0');

            // rect의 네 모서리를 변환
            const [x1, y1] = applyMatrixToCoordinates(currentMatrix, x, y);
            const [x2, y2] = applyMatrixToCoordinates(currentMatrix, x + width, y);
            const [x3, y3] = applyMatrixToCoordinates(currentMatrix, x + width, y + height);
            const [x4, y4] = applyMatrixToCoordinates(currentMatrix, x, y + height);

            // 단순한 변환(회전/기울임 없음)인 경우 rect 유지, 아니면 path로 변환
            const isSimpleTransform = (currentMatrix[1] ?? 0) === 0 && (currentMatrix[2] ?? 0) === 0;

            if (isSimpleTransform) {
              const newX = roundToPrecision(Math.min(x1, x2, x3, x4));
              const newY = roundToPrecision(Math.min(y1, y2, y3, y4));
              const newWidth = roundToPrecision(Math.abs(x2 - x1));
              const newHeight = roundToPrecision(Math.abs(y3 - y2));

              attrs.x = newX.toString();
              attrs.y = newY.toString();
              attrs.width = newWidth.toString();
              attrs.height = newHeight.toString();
            } else {
              // path로 변환
              const pathData =
                `M${roundToPrecision(x1)},${roundToPrecision(y1)} ` +
                `L${roundToPrecision(x2)},${roundToPrecision(y2)} ` +
                `L${roundToPrecision(x3)},${roundToPrecision(y3)} ` +
                `L${roundToPrecision(x4)},${roundToPrecision(y4)} Z`;

              return {
                ...node,
                name: 'path',
                attributes: { ...attrs, d: pathData },
                children: node.children?.map((child) => transformNode(child, [1, 0, 0, 1, 0, 0])) ?? [],
              };
            }
          }
          break;
        case 'circle':
          if (attrs.cx !== undefined && attrs.cy !== undefined) {
            const cx = parseFloat(attrs.cx) || 0;
            const cy = parseFloat(attrs.cy) || 0;
            const r = parseFloat(attrs.r || '0');

            const [newCx, newCy] = applyMatrixToCoordinates(currentMatrix, cx, cy);
            const scaleX = Math.sqrt(
              (currentMatrix[0] ?? 0) * (currentMatrix[0] ?? 0) +
                (currentMatrix[1] ?? 0) * (currentMatrix[1] ?? 0),
            );
            const scaleY = Math.sqrt(
              (currentMatrix[2] ?? 0) * (currentMatrix[2] ?? 0) +
                (currentMatrix[3] ?? 0) * (currentMatrix[3] ?? 0),
            );
            const avgScale = (scaleX + scaleY) / 2;

            attrs.cx = roundToPrecision(newCx).toString();
            attrs.cy = roundToPrecision(newCy).toString();
            attrs.r = roundToPrecision(r * avgScale).toString();
          }
          break;
        case 'ellipse':
          if (attrs.cx !== undefined && attrs.cy !== undefined) {
            const cx = parseFloat(attrs.cx) || 0;
            const cy = parseFloat(attrs.cy) || 0;
            const rx = parseFloat(attrs.rx || '0');
            const ry = parseFloat(attrs.ry || '0');

            const [newCx, newCy] = applyMatrixToCoordinates(currentMatrix, cx, cy);
            const scaleX = Math.sqrt(
              (currentMatrix[0] ?? 0) * (currentMatrix[0] ?? 0) +
                (currentMatrix[1] ?? 0) * (currentMatrix[1] ?? 0),
            );
            const scaleY = Math.sqrt(
              (currentMatrix[2] ?? 0) * (currentMatrix[2] ?? 0) +
                (currentMatrix[3] ?? 0) * (currentMatrix[3] ?? 0),
            );

            attrs.cx = roundToPrecision(newCx).toString();
            attrs.cy = roundToPrecision(newCy).toString();
            attrs.rx = roundToPrecision(rx * scaleX).toString();
            attrs.ry = roundToPrecision(ry * scaleY).toString();
          }
          break;
        case 'line':
          if (
            attrs.x1 !== undefined &&
            attrs.y1 !== undefined &&
            attrs.x2 !== undefined &&
            attrs.y2 !== undefined
          ) {
            const x1 = parseFloat(attrs.x1) || 0;
            const y1 = parseFloat(attrs.y1) || 0;
            const x2 = parseFloat(attrs.x2) || 0;
            const y2 = parseFloat(attrs.y2) || 0;

            const [newX1, newY1] = applyMatrixToCoordinates(currentMatrix, x1, y1);
            const [newX2, newY2] = applyMatrixToCoordinates(currentMatrix, x2, y2);

            attrs.x1 = roundToPrecision(newX1).toString();
            attrs.y1 = roundToPrecision(newY1).toString();
            attrs.x2 = roundToPrecision(newX2).toString();
            attrs.y2 = roundToPrecision(newY2).toString();
          }
          break;
        case 'polyline':
        case 'polygon':
          if (attrs.points) {
            const points = attrs.points
              .trim()
              .split(/\s+/)
              .map((pt) => {
                const coords = pt.split(',');
                if (coords.length === 2) {
                  const x = parseFloat(coords[0]!);
                  const y = parseFloat(coords[1]!);
                  if (!isNaN(x) && !isNaN(y)) {
                    const [newX, newY] = applyMatrixToCoordinates(currentMatrix, x, y);
                    return `${roundToPrecision(newX)},${roundToPrecision(newY)}`;
                  }
                }
                return pt;
              });
            attrs.points = points.join(' ');
          }
          break;
        case 'text':
          if (attrs.x !== undefined && attrs.y !== undefined) {
            const x = parseFloat(attrs.x) || 0;
            const y = parseFloat(attrs.y) || 0;

            const [newX, newY] = applyMatrixToCoordinates(currentMatrix, x, y);
            attrs.x = roundToPrecision(newX).toString();
            attrs.y = roundToPrecision(newY).toString();

            if (attrs['font-size']) {
              const fontSize = parseFloat(attrs['font-size']);
              if (!isNaN(fontSize)) {
                const scaleX = Math.sqrt(
                  (currentMatrix[0] ?? 0) * (currentMatrix[0] ?? 0) +
                    (currentMatrix[1] ?? 0) * (currentMatrix[1] ?? 0),
                );
                const scaleY = Math.sqrt(
                  (currentMatrix[2] ?? 0) * (currentMatrix[2] ?? 0) +
                    (currentMatrix[3] ?? 0) * (currentMatrix[3] ?? 0),
                );
                const avgScale = (scaleX + scaleY) / 2;
                attrs['font-size'] = roundToPrecision(fontSize * avgScale).toString();
              }
            }
          }
          break;
        case 'image':
          if (attrs.x !== undefined && attrs.y !== undefined) {
            const x = parseFloat(attrs.x) || 0;
            const y = parseFloat(attrs.y) || 0;
            const width = parseFloat(attrs.width || '0');
            const height = parseFloat(attrs.height || '0');

            const [newX, newY] = applyMatrixToCoordinates(currentMatrix, x, y);
            const scaleX = Math.sqrt(
              (currentMatrix[0] ?? 0) * (currentMatrix[0] ?? 0) +
                (currentMatrix[1] ?? 0) * (currentMatrix[1] ?? 0),
            );
            const scaleY = Math.sqrt(
              (currentMatrix[2] ?? 0) * (currentMatrix[2] ?? 0) +
                (currentMatrix[3] ?? 0) * (currentMatrix[3] ?? 0),
            );

            attrs.x = roundToPrecision(newX).toString();
            attrs.y = roundToPrecision(newY).toString();
            attrs.width = roundToPrecision(width * scaleX).toString();
            attrs.height = roundToPrecision(height * scaleY).toString();
          }
          break;
      }

      // 자식 노드들을 현재 매트릭스로 변환 (단위 행렬이 아닌 경우에만)
      const transformedChildren =
        node.children?.map((child) =>
          transformNode(child, isIdentityMatrix(currentMatrix) ? [1, 0, 0, 1, 0, 0] : currentMatrix),
        ) ?? [];

      return { ...node, attributes: attrs, children: transformedChildren };
    };

    // Apply global transform if provided
    const initialMatrix =
      globalTransform && globalTransform.length === 6 ? globalTransform : [1, 0, 0, 1, 0, 0];

    // 전체 SVG 변환
    const transformedSvg = transformNode(svgJson, initialMatrix);

    // Return the appropriate type based on input
    if (typeof svgStringOrJson === 'string') {
      return stringify(transformedSvg);
    } else {
      return transformedSvg;
    }
  }

  /**
   * Normalize SVG viewBox when missing by using SVG's width and height attributes
   */
  static async normalizeViewBox(svgString: string): Promise<string>;
  /**
   * Normalize SVG viewBox when missing by using SVG's width and height attributes
   */
  static async normalizeViewBox(svgJson: INode): Promise<INode>;
  static async normalizeViewBox(svgStringOrJson: string | INode): Promise<string | INode> {
    let svgJson: INode;
    let svgWidth: number;
    let svgHeight: number;

    if (typeof svgStringOrJson === 'string') {
      svgJson = await parse(svgStringOrJson);
      const size = this.extractSvgSize(svgStringOrJson);
      svgWidth = size.width;
      svgHeight = size.height;
    } else {
      svgJson = svgStringOrJson;
      // INode에서 width/height 추출
      const widthAttr = svgJson.attributes?.width;
      const heightAttr = svgJson.attributes?.height;

      if (!widthAttr || !heightAttr) {
        throw new Error('SVG must have width and height attributes for viewBox normalization');
      }

      svgWidth = parseFloat(widthAttr);
      svgHeight = parseFloat(heightAttr);

      if (isNaN(svgWidth) || isNaN(svgHeight) || svgWidth <= 0 || svgHeight <= 0) {
        throw new Error('Invalid SVG width or height attributes');
      }
    }

    // viewBox가 이미 존재하는지 확인
    const currentViewBox = svgJson.attributes?.viewBox;
    if (currentViewBox && typeof currentViewBox === 'string' && currentViewBox.trim()) {
      // viewBox가 이미 존재하면 그대로 반환
      if (typeof svgStringOrJson === 'string') {
        return stringify(svgJson);
      } else {
        return svgJson;
      }
    }

    // viewBox가 없으면 width, height를 기반으로 생성
    const normalizedSvgJson: INode = {
      ...svgJson,
      attributes: {
        ...svgJson.attributes,
        viewBox: `0 0 ${svgWidth} ${svgHeight}`,
      },
    };

    // Return the appropriate type based on input
    if (typeof svgStringOrJson === 'string') {
      return stringify(normalizedSvgJson);
    } else {
      return normalizedSvgJson;
    }
  }

  /**
   * SVG 문자열에서 href로 링크된 image 요소들을 찾아서 base64 embed 형태로 변환합니다.
   * 이미지의 렌더링 위치, 크기 정보는 기존과 동일하게 유지됩니다.
   * @param svgString - 원본 SVG 문자열
   * @returns Promise<string> - href 링크가 base64 embed로 변환된 SVG 문자열
   */
  static async convertImageLinkToEmbedInSvg(svgString: string): Promise<string> {
    const svgJson = await parse(svgString);

    // 재귀적으로 image 요소를 찾아서 href를 base64로 변환하는 함수
    const convertImageNodes = async (node: INode): Promise<INode> => {
      // image 요소인 경우 href 속성을 확인하고 변환
      if (node.name === 'image' && node.attributes?.href) {
        const href = node.attributes.href;

        // 이미 data URI인 경우 그대로 반환
        if (href.startsWith('data:')) {
          return node;
        }

        try {
          // URL을 base64 datauri로 변환
          const dataUri = await this.urlToDataUriCached(href);

          // 새로운 node 생성 (기존 속성은 모두 유지하고 href만 변경)
          return {
            ...node,
            attributes: {
              ...node.attributes,
              href: dataUri,
            },
          };
        } catch (error) {
          // 변환 실패 시 원본 그대로 반환하고 경고 출력
          console.warn(`Failed to convert image URL to base64: ${href}`, error);
          return node;
        }
      }

      // 자식 노드들도 재귀적으로 처리
      if (node.children && node.children.length > 0) {
        const convertedChildren = await Promise.all(node.children.map((child) => convertImageNodes(child)));

        return {
          ...node,
          children: convertedChildren,
        };
      }

      // image 요소가 아니거나 자식이 없는 경우 그대로 반환
      return node;
    };

    // SVG 전체를 변환
    const convertedSvgJson = await convertImageNodes(svgJson);

    // 변환된 SVG JSON을 문자열로 변환하여 반환
    return stringify(convertedSvgJson);
  }

  /**
   * SVG의 viewBox margin을 확장합니다.
   * @param svgString - 원본 SVG 문자열
   * @param marginPx - 확장할 margin 크기(픽셀)
   * @returns string - viewBox margin이 확장된 SVG 문자열
   */
  static expandViewBoxMargin(
    svgString: string,
    marginPx: number,
    postProcessFn?: (svg: string) => string,
  ): string {
    if (marginPx <= 0) {
      return svgString;
    }

    // SVG width, height 추출
    const svgTagMatch = svgString.match(/<svg[^>]*>/i);
    if (!svgTagMatch) throw new Error('No <svg> tag found');

    const svgTag = svgTagMatch[0];
    const widthMatch = svgTag.match(/width\s*=\s*['"]([\d.]+)['"]/i);
    const heightMatch = svgTag.match(/height\s*=\s*['"]([\d.]+)['"]/i);

    if (!widthMatch || !heightMatch || !widthMatch[1] || !heightMatch[1]) {
      throw new Error('SVG must have width and height attributes');
    }

    const svgWidth = parseFloat(widthMatch[1]);
    const svgHeight = parseFloat(heightMatch[1]);

    // viewBox 추출 및 파싱
    const viewBoxMatch = svgTag.match(/viewBox\s*=\s*['"]([\d.\s-]+)['"]/i);
    let vbX = 0;
    let vbY = 0;
    let vbWidth = svgWidth;
    let vbHeight = svgHeight;

    if (viewBoxMatch && viewBoxMatch[1]) {
      const parts = viewBoxMatch[1].split(/\s+/).map(Number);
      if (parts.length === 4 && parts.every((v) => !isNaN(v))) {
        vbX = parts[0] ?? 0;
        vbY = parts[1] ?? 0;
        vbWidth = parts[2] ?? svgWidth;
        vbHeight = parts[3] ?? svgHeight;
      }
    }

    // viewBox margin 확장
    const newVbX = vbX - marginPx;
    const newVbY = vbY - marginPx;
    const newVbWidth = vbWidth + 2 * marginPx;
    const newVbHeight = vbHeight + 2 * marginPx;
    const newViewBox = `${newVbX} ${newVbY} ${newVbWidth} ${newVbHeight}`;

    // viewBox 업데이트
    let newSvgTag: string;
    if (viewBoxMatch) {
      // 기존 viewBox 교체
      newSvgTag = svgTag.replace(/viewBox\s*=\s*['"][^'"]*['"]/i, `viewBox="${newViewBox}"`);
    } else {
      // viewBox 추가
      newSvgTag = svgTag.replace(/<svg/i, `<svg viewBox="${newViewBox}"`);
    }

    const expandedSvg = svgString.replace(svgTag, newSvgTag);

    return postProcessFn ? postProcessFn(expandedSvg) : expandedSvg;
  }

  /**
   * Expand SVG canvas by total padding(px) without scaling paths.
   * - Expands viewBox by pad on all sides
   * - Also expands width/height by 2*pad to keep apparent scale unchanged
   * - Handles unsafe inputs inside (negative/float/NaN)
   */
  static expandSvg(svgString: string, totalMarginPx: number): string {
    const pad = Math.max(
      0,
      Math.round(Number.isFinite(totalMarginPx as number) ? (totalMarginPx as number) : 0),
    );
    if (pad === 0) return svgString;

    const svgTagMatch = svgString.match(/<svg[^>]*>/i);
    if (!svgTagMatch) throw new Error('No <svg> tag found');
    const svgTag = svgTagMatch[0];

    // Extract width/height (fallback to viewBox if missing)
    const widthMatch = svgTag.match(/width\s*=\s*['"]([\d.]+)['"]/i);
    const heightMatch = svgTag.match(/height\s*=\s*['"]([\d.]+)['"]/i);

    // Extract or fallback viewBox
    const viewBoxMatch = svgTag.match(/viewBox\s*=\s*['"]([\d.\s-]+)['"]/i);
    let vbX = 0;
    let vbY = 0;
    let vbWidth: number | undefined;
    let vbHeight: number | undefined;

    if (viewBoxMatch && viewBoxMatch[1]) {
      const tokens = viewBoxMatch[1].trim().split(/\s+/);
      const vx = parseFloat(tokens[0] ?? '0');
      const vy = parseFloat(tokens[1] ?? '0');
      const vw = parseFloat(tokens[2] ?? 'NaN');
      const vh = parseFloat(tokens[3] ?? 'NaN');
      vbX = Number.isFinite(vx) ? vx : 0;
      vbY = Number.isFinite(vy) ? vy : 0;
      vbWidth = Number.isFinite(vw) ? vw : undefined;
      vbHeight = Number.isFinite(vh) ? vh : undefined;
    }

    if (!Number.isFinite(vbWidth ?? NaN) || !Number.isFinite(vbHeight ?? NaN)) {
      const svgWidth = widthMatch?.[1] ? parseFloat(widthMatch[1]) : NaN;
      const svgHeight = heightMatch?.[1] ? parseFloat(heightMatch[1]) : NaN;
      if (!Number.isFinite(svgWidth) || !Number.isFinite(svgHeight)) {
        throw new Error('SVG must have width/height or valid viewBox');
      }
      vbWidth = svgWidth;
      vbHeight = svgHeight;
      vbX = 0;
      vbY = 0;
    }

    const safeVbW = Number.isFinite(vbWidth ?? NaN) ? (vbWidth as number) : 0;
    const safeVbH = Number.isFinite(vbHeight ?? NaN) ? (vbHeight as number) : 0;
    const newVbX = vbX - pad;
    const newVbY = vbY - pad;
    const newVbW = safeVbW + pad * 2;
    const newVbH = safeVbH + pad * 2;

    const newViewBox = `${newVbX} ${newVbY} ${newVbW} ${newVbH}`;

    // Update or insert viewBox
    let newSvgTag = svgTag;
    if (viewBoxMatch) {
      newSvgTag = newSvgTag.replace(/viewBox\s*=\s*['"][^'"]*['"]/i, `viewBox="${newViewBox}"`);
    } else {
      newSvgTag = newSvgTag.replace(/<svg/i, `<svg viewBox="${newViewBox}"`);
    }

    // Update width/height if present
    if (widthMatch) {
      const oldW = parseFloat(widthMatch[1] || '0');
      const newW = Math.max(1, Math.round(oldW + pad * 2));
      newSvgTag = newSvgTag.replace(/width\s*=\s*['"][^'"]*['"]/i, `width="${newW}"`);
    }
    if (heightMatch) {
      const oldH = parseFloat(heightMatch[1] || '0');
      const newH = Math.max(1, Math.round(oldH + pad * 2));
      newSvgTag = newSvgTag.replace(/height\s*=\s*['"][^'"]*['"]/i, `height="${newH}"`);
    }

    return svgString.replace(svgTag, newSvgTag);
  }

  /**
   * Add a circle marker at the given topPoint on the cutline SVG.
   * @param svgString - Target SVG string
   * @param topPoint - Center position of the circle
   * @param circleRadius - Circle radius in px
   * @param fillColor - Circle fill color
   */
  static async addCircleOnCutline({
    svgString,
    topPoint,
    circleRadius,
    fillColor,
  }: {
    svgString: string;
    topPoint: { x: number; y: number };
    circleRadius: number;
    fillColor: string;
  }): Promise<string> {
    const svgJson = await parse(svgString);
    if (svgJson.name !== 'svg') throw new Error('Invalid SVG: root <svg> not found');

    const circleNode: INode = {
      name: 'circle',
      type: 'element',
      value: '',
      attributes: {
        cx: String(topPoint.x),
        cy: String(topPoint.y),
        r: String(circleRadius),
        fill: String(fillColor),
      },
      children: [],
    };

    const children = Array.isArray(svgJson.children) ? svgJson.children : [];
    svgJson.children = [...children, circleNode];

    return stringify(svgJson);
  }

  /*
  url 을 받아서 svg 를 fetch 해서 문자열로 반환하는 downloadSvgFromUrl method 만들어 주세요.
  */
  static async downloadSvgFromUrl(url: string): Promise<string> {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to download SVG from URL: ${response.statusText}`);
    return await response.text();
  }

  /**
   * Find the topmost point (minimum y) among all <path> elements in the SVG string.
   * - All transforms are flattened beforehand (CTM considered).
   * - Hidden paths (display="none", visibility="hidden", opacity="0") are skipped.
   * - Arcs and shorthand commands are normalized to cubic/absolute commands.
   * @param svgString - SVG source as string
   * @returns Promise<{ x: number; y: number }>
   */
  static async findTopPointInSvgPath({
    svgString,
  }: {
    svgString: string;
  }): Promise<{ x: number; y: number }> {
    const prof = new Prof();
    const startAll = performance.now();

    const sanitized = svgString
      .replace(/xlink:href="data:[^"]*"/g, 'xlink:href="__DATA__"')
      .replace(/href="data:[^"]*"/g, 'href="__DATA__"');

    const slim = sanitized
      .replace(/<metadata[\s\S]*?<\/metadata>/gi, '')
      .replace(/<style[\s\S]*?<\/style>/gi, '');

    // 1) svgson 파싱 및 transform flatten
    prof.mark('1.parse(svgson)');
    // const svgJson = await parse(svgString);
    const svgJson = await parse(slim);
    prof.end('1.parse(svgson)');

    prof.mark('2.flattenTransformAttributes');
    const flattened = (await this.flattenTransformAttributes(svgJson)) as INode;
    prof.end('2.flattenTransformAttributes');

    const EPS = 1e-9;

    const clamp01 = (t: number) => (t < 0 ? 0 : t > 1 ? 1 : t);
    const evalQuadratic = (p0: number, p1: number, p2: number, t: number): number => {
      const mt = 1 - t;
      return mt * mt * p0 + 2 * mt * t * p1 + t * t * p2;
    };
    const evalCubic = (p0: number, p1: number, p2: number, p3: number, t: number): number => {
      const mt = 1 - t;
      return mt * mt * mt * p0 + 3 * mt * mt * t * p1 + 3 * mt * t * t * p2 + t * t * t * p3;
    };
    const pushIfBetter = (
      best: { x: number; y: number } | null,
      x: number,
      y: number,
    ): { x: number; y: number } | null => {
      if (!Number.isFinite(x) || !Number.isFinite(y)) return best;
      return !best || y < best.y ? { x, y } : best;
    };

    let globalBest: { x: number; y: number } | null = null;

    const visit = (node: INode): void => {
      if (!node || typeof node !== 'object') return;
      const a = node.attributes ?? {};

      // 기본 가시성 필터: 숨김 요소 -> 처리 안함 (skip)
      if (
        (a.display && String(a.display) === 'none') ||
        (a.visibility && String(a.visibility) === 'hidden') ||
        a.opacity === '0'
      ) {
        return;
      }

      if (node.name === 'path' && a.d) {
        // svgpath 정규화 시간
        prof.mark('3.svgpath.normalize');
        const norm = svgpath(a.d).unshort().unarc().abs() as any;
        const normMs = prof.end('3.svgpath.normalize');
        prof.addSection('svgpath.normalize', normMs);

        const iterate: unknown = (norm as any).iterate?.bind(norm);
        if (typeof iterate !== 'function') {
          // iterate 미지원 시 패스 (svgpath 버전 상 iterate는 제공되지만 타입 선언이 없을 수 있음) -> 처리 안함 (skip)
        } else {
          let subpathStartX = 0;
          let subpathStartY = 0;
          let localBest: { x: number; y: number } | null = null;

          // path 단위 iterate 시간
          prof.mark('4.iterate.path');
          (
            iterate as (
              cb: (seg: (string | number)[], idx: number, prevX: number, prevY: number) => void,
            ) => void
          )((seg, _idx, prevX, prevY) => {
            const type = String(seg[0]);
            const commit = (x: number, y: number) => {
              localBest = pushIfBetter(localBest, x, y);
            };

            switch (type) {
              case 'M': {
                const x = Number(seg[1]);
                const y = Number(seg[2]);
                subpathStartX = x;
                subpathStartY = y;
                commit(x, y);
                break;
              }
              case 'L': {
                const x1 = Number(seg[1]);
                const y1 = Number(seg[2]);
                commit(prevX, prevY);
                commit(x1, y1);
                break;
              }
              case 'H': {
                const x1 = Number(seg[1]);
                commit(prevX, prevY);
                commit(x1, prevY);
                break;
              }
              case 'V': {
                const y1 = Number(seg[1]);
                commit(prevX, prevY);
                commit(prevX, y1);
                break;
              }
              case 'Q': {
                const cx = Number(seg[1]);
                const cy = Number(seg[2]);
                const x1 = Number(seg[3]);
                const y1 = Number(seg[4]);

                // y'(t) = 2 * ((cy - prevY) + t * (prevY - 2*cy + y1)) = 0
                const qa = prevY - 2 * cy + y1;
                const qb = cy - prevY;
                const candidates: number[] = [0, 1];
                if (Math.abs(qa) > EPS) {
                  const t = -qb / qa;
                  if (t > 0 && t < 1) candidates.push(t);
                }
                for (const t0 of candidates) {
                  const t = clamp01(t0);
                  const xt = evalQuadratic(prevX, cx, x1, t);
                  const yt = evalQuadratic(prevY, cy, y1, t);
                  commit(xt, yt);
                }
                break;
              }
              case 'C': {
                const x1 = Number(seg[1]);
                const y1 = Number(seg[2]);
                const x2 = Number(seg[3]);
                const y2 = Number(seg[4]);
                const x3 = Number(seg[5]);
                const y3 = Number(seg[6]);

                // y'(t) = 3*Ay*t^2 + 2*By*t + Cy = 0 (Beziér 미분)
                const Ay = -prevY + 3 * y1 - 3 * y2 + y3;
                const By = 3 * prevY - 6 * y1 + 3 * y2;
                const Cy = -3 * prevY + 3 * y1;

                const a = 3 * Ay;
                const b = 2 * By;
                const c = Cy;

                const candidates: number[] = [0, 1];
                if (Math.abs(a) < EPS) {
                  if (Math.abs(b) > EPS) {
                    const t = -c / b;
                    if (t > 0 && t < 1) candidates.push(t);
                  }
                } else {
                  const disc = b * b - 4 * a * c;
                  if (disc >= 0) {
                    const s = Math.sqrt(disc);
                    const t1 = (-b + s) / (2 * a);
                    const t2 = (-b - s) / (2 * a);
                    if (t1 > 0 && t1 < 1) candidates.push(t1);
                    if (t2 > 0 && t2 < 1) candidates.push(t2);
                  }
                }

                for (const t0 of candidates) {
                  const t = clamp01(t0);
                  const xt = evalCubic(prevX, x1, x2, x3, t);
                  const yt = evalCubic(prevY, y1, y2, y3, t);
                  commit(xt, yt);
                }
                break;
              }
              case 'Z':
              case 'z': {
                // close path: 선분(prev -> start)
                commit(prevX, prevY);
                commit(subpathStartX, subpathStartY);
                break;
              }
              default: {
                // 기타 명령은 끝점들만 보수적으로 평가
                const raw = (seg as (string | number)[]).slice(1);
                for (let i = 0; i + 1 < raw.length; i += 2) {
                  const nx = Number(raw[i]);
                  const ny = Number(raw[i + 1]);
                  if (Number.isFinite(nx) && Number.isFinite(ny)) {
                    commit(nx, ny);
                  }
                }
              }
            }
          });

          if (localBest) {
            globalBest = pushIfBetter(globalBest, localBest.x, localBest.y);
          }
        }
      }

      // 자식 순회 (recursive)
      if (Array.isArray(node.children)) node.children.forEach(visit);
    };

    prof.mark('3.visit(all)');
    visit(flattened);
    prof.end('3.visit(all)');

    if (!globalBest) {
      prof.report();
      throw new Error('No <path> elements found in SVG');
    }

    const totalMs = performance.now() - startAll;
    prof.addSection('TOTAL', totalMs);
    prof.report();

    return globalBest;
  }

  static injectDropShadow(svg: string): string {
    const { dx, dy, stdDeviation, color, opacity, filterId } = CUTLINER_OPTS.DEFAULT_SHADOW;

    // const blurId = `${filterId}-blur`;
    // const offsetId = `${filterId}-offset`;
    // const floodId = `${filterId}-flood`;

    const filterDef =
      `<filter id="${filterId}" x="-50%" y="-50%" width="200%" height="200%" filterUnits="objectBoundingBox">` +
      `<feGaussianBlur in="SourceAlpha" stdDeviation="${stdDeviation}" result="blur"/>` +
      `<feOffset in="blur" dx="${dx}" dy="${dy}" result="offsetBlur"/>` +
      `<feFlood flood-color="${color}" flood-opacity="${opacity}" result="color"/>` +
      `<feComposite in="color" in2="offsetBlur" operator="in" result="shadow"/>` +
      `<feMerge>` +
      `<feMergeNode in="shadow"/>` +
      `<feMergeNode in="SourceGraphic"/>` +
      `</feMerge>` +
      `</filter>`;

    if (/<defs>/.test(svg)) {
      svg = svg.replace(/<defs>([\s\S]*?)<\/defs>/, `<defs>$1${filterDef}</defs>`);
    } else {
      svg = svg.replace(/<svg([^>]*)>/, `<svg$1><defs>${filterDef}</defs>`);
    }

    //  기존 stroke 관련 속성 제거
    svg = svg.replace(/\sstroke-dasharray="[^"]*"/g, '');
    svg = svg.replace(/stroke-dasharray:\s*[^;"']+;?/g, '');
    svg = svg.replace(/\sstroke="[^"]*"/g, '');
    svg = svg.replace(/stroke:\s*[^;"']+;?/g, '');

    svg = svg.replace(/<path\b/g, `<path filter="url(#${filterId})"`);

    return svg;
  }

  /**
   * SVG 속성 문자열에서 지정된 속성의 px 값을 300 DPI mm로 변환합니다.
   * unit이 이미 있는 경우 변환하지 않습니다.
   *
   * @param attributes - SVG 속성 문자열
   * @param attributeName - 변환할 속성 이름 (예: 'width', 'height')
   * @returns 변환된 속성 문자열과 변환 여부
   */
  private static convertAttributeToDpi300Mm(
    attributes: string,
    attributeName: string,
  ): { newAttributes: string; modified: boolean } {
    const attrPattern = new RegExp(`${attributeName}="([^"]+)"`);
    const attrMatch = attributes.match(attrPattern);

    if (!attrMatch || !attrMatch[1]) {
      return { newAttributes: attributes, modified: false };
    }

    const attrValue = attrMatch[1];

    // unit이 없는 경우만 처리 (숫자만 있는 경우 = px)
    if (!/^\d+(\.\d+)?$/.test(attrValue)) {
      return { newAttributes: attributes, modified: false };
    }

    const pxValue = parseFloat(attrValue);
    const mmValue = ((pxValue * 25.4) / 300).toFixed(4);
    const newAttributes = attributes.replace(attrPattern, `${attributeName}="${mmValue}mm"`);

    return { newAttributes, modified: true };
  }

  /**
   * SVG 문자열의 width, height 속성을 px에서 300 DPI 기준 mm 단위로 변환합니다.
   * unit이 이미 있는 경우 무시하고 원본을 반환합니다.
   * unit이 없으면 px로 가정하여 300 DPI mm로 변환합니다.
   *
   * 변환 공식: mm = px * 25.4 / 300
   *
   * @param svgString - 원본 SVG 문자열
   * @returns string - width, height가 mm 단위로 변환된 SVG 문자열
   */
  static convertSvgSizeToDpi300Mm(svgString: string): string {
    // SVG 태그에서 width, height 추출
    const svgTagMatch = svgString.match(/<svg([^>]*)>/);
    if (!svgTagMatch || !svgTagMatch[1]) return svgString;

    const svgAttributes = svgTagMatch[1];
    let modified = false;
    let newAttributes = svgAttributes;

    // width 처리
    const widthResult = this.convertAttributeToDpi300Mm(newAttributes, 'width');
    newAttributes = widthResult.newAttributes;
    modified = modified || widthResult.modified;

    // height 처리
    const heightResult = this.convertAttributeToDpi300Mm(newAttributes, 'height');
    newAttributes = heightResult.newAttributes;
    modified = modified || heightResult.modified;

    if (!modified) return svgString;

    return svgString.replace(/<svg[^>]*>/, `<svg${newAttributes}>`);
  }

  /**
   * SVG 크기 속성 값을 px 단위 숫자로 파싱합니다.
   * @param value - SVG width/height 속성 값 (예: "300", "300px", "100mm")
   * @returns number | null - px 단위 숫자, 파싱 불가능한 경우 null
   */
  private static parseSvgDimensionValue(value: string | undefined): number | null {
    if (!value) return null;

    // "300" 또는 "300px" 형태 파싱
    const match = value.match(/^(\d+(?:\.\d+)?)(px)?$/);
    if (match && match[1]) {
      return parseFloat(match[1]);
    }

    return null; // 단위가 있거나 파싱 불가능한 경우
  }

  /**
   * 이미지 URL과 SVG URL을 받아서 이미지를 SVG의 첫 번째 자식 요소로 추가합니다.
   * 이미지는 base64 data URI로 변환되어 xlink:href로 삽입됩니다.
   * 이미지는 자체 크기를 유지하며 SVG 내에서 가로/세로 중앙정렬됩니다.
   *
   * @param imageUrl - 삽입할 이미지의 URL
   * @param svgUrl - 베이스로 사용할 SVG의 URL
   * @param to300dpi - true인 경우 SVG의 width, height를 300 DPI mm 단위로 변환 (optional)
   * @returns Promise<string> - 이미지가 결합된 SVG 문자열
   */
  static async combineImageIntoSvg(imageUrl: string, svgUrl: string, to300dpi?: boolean): Promise<string> {
    // 1. 병렬로 리소스 다운로드 및 이미지 크기 얻기
    const [svgString, dataUri, imageDimensions] = await Promise.all([
      this.downloadSvgFromUrl(svgUrl),
      this.urlToDataUriCached(imageUrl),
      ImageUtil.getImageDimensions(imageUrl),
    ]);

    // 2. SVG 파싱
    const svgJson = await parse(svgString);

    // 3. SVG 크기 정보 파싱
    const svgWidth = this.parseSvgDimensionValue(svgJson.attributes.width);
    const svgHeight = this.parseSvgDimensionValue(svgJson.attributes.height);

    // 4. 중앙정렬 위치 계산
    let x = 0;
    let y = 0;

    if (svgWidth !== null && svgHeight !== null) {
      x = Math.max(0, (svgWidth - imageDimensions.width) / 2);
      y = Math.max(0, (svgHeight - imageDimensions.height) / 2);
    }

    // 5. image 요소 생성 (이미지 자체 크기 사용, 중앙정렬)
    const imageElement: INode = {
      name: 'image',
      type: 'element',
      value: '',
      attributes: {
        'xlink:href': dataUri,
        // href: dataUri,
        width: imageDimensions.width.toString(),
        height: imageDimensions.height.toString(),
        x: x.toString(),
        y: y.toString(),
      },
      children: [],
    };

    // 6. 첫 번째 자식으로 추가
    svgJson.children.unshift(imageElement);

    // 7. SVG 문자열로 변환
    let resultSvg = stringify(svgJson);

    // 8. 300 DPI mm 변환 (옵션)
    if (to300dpi) {
      resultSvg = this.convertSvgSizeToDpi300Mm(resultSvg);
    }

    return resultSvg;
  }
}
